/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React from "react";

import { RenderElementProps } from "slate-react";
import styled from "styled-components";

import { TextBlockSubType } from "@vienna/common";

import { Tooltip } from "src/components/UI/Tooltip";
import { CustomElement } from "src/declare";

export function Element(props: RenderElementProps & { subType: TextBlockSubType; readOnly: boolean }) {
  const { attributes, children, subType, readOnly } = props;
  const element = props.element as CustomElement;

  const renderElement = () => {
    switch (element.type) {
      case "bulleted-list":
        return (
          <ul style={{ paddingLeft: "20px" }} {...attributes}>
            {children}
          </ul>
        );
      case "numbered-list":
        return (
          <ol start={element.start} {...attributes} style={{ paddingLeft: "40px" }}>
            {children}
          </ol>
        );
      case "list-item":
        return <li {...attributes}>{children}</li>;
      default:
        return children;
    }
  };
  if (element.type === "link") {
    return (
      <Tooltip tooltipText={`Link: ${element.url}`}>
        <StyledLink onClick={() => window.open(element.url)} textSubtype={subType || "plain"} {...attributes}>
          {children}
        </StyledLink>
      </Tooltip>
    );
  }

  return (
    <StyledElement $readOnly={readOnly} textSubtype={element.type !== "list-item" && subType ? subType : "plain"}>
      {renderElement()}
    </StyledElement>
  );
}

const StyledLink = styled.div<{ textSubtype: TextBlockSubType; scale?: number }>`
  cursor: pointer;
  :hover {
    text-decoration: underline;
  }
  color: ${({ theme }) => theme.text.url};
`;

const StyledElement = styled.div<{ $readOnly: boolean; textSubtype: TextBlockSubType; scale?: number }>`
  border-left: ${({ textSubtype, theme }) => textSubtype === "quote" && `3px solid ${theme.block.border}`};
  padding-left: ${({ textSubtype }) => textSubtype === "quote" && "10px"};
  line-height: ${({ textSubtype }) => textSubtype === "quote" && "21px"};
`;
